'use client';

import { EcEnv } from '@castlery/config';
import { Box, Button, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { Chat, Whatsapp } from '@castlery/fortress/Icons';
import React, { useState, useEffect } from 'react';
import { GladlyLoadScript } from './gladly-inject';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectedActiveUser } from '@castlery/modules-user-domain';

const GladlyBox = () => {
  const { mobile } = useBreakpoints();
  const [active, setActive] = useState(false);
  const [hide, setHide] = useState(false);
  const currentUser = useAppSelector(selectedActiveUser);

  useEffect(() => {
    if (EcEnv.NEXT_PUBLIC_COUNTRY === 'SG') {
      setActive(true);
    }
    window?.Gladly?.navigate && window?.Gladly?.navigate();
  }, []);

  useEffect(() => {
    if (active) {
      let attempts = 0;
      const maxAttempts = 10; // 最多尝试 10 秒

      const intervalId = window.setInterval(() => {
        attempts++;

        // 超时保护：如果 10 秒后还没初始化，放弃尝试
        if (attempts > maxAttempts) {
          window.clearInterval(intervalId);
          console.warn('Gladly SDK initialization timeout');
          return;
        }

        try {
          // 更严格的初始化检查
          if (window.Gladly && typeof window.Gladly?.on === 'function' && typeof window.Gladly?.show === 'function') {
            window.Gladly.on('sidekick:opened', () => {
              setHide(true);
            });
            window.Gladly.on('sidekick:closed', () => {
              setHide(false);
            });
            window.clearInterval(intervalId);
          }
        } catch (error) {
          // 捕获并忽略初始化错误，继续尝试
          console.warn('Gladly SDK not fully initialized yet:', error);
        }
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [active]);

  return (
    <>
      <GladlyLoadScript
        handleOnReady={() => {
          setActive(true);
        }}
      />
      {active && (
        <Box
          sx={[
            {
              position: 'fixed',
              backgroundColor: 'rgba(255, 255, 255, .9)',
              zIndex: 999,
              right: 0,
              width: mobile ? '43px' : '70px',
              bottom: mobile ? '110px' : '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: hide ? 0 : 1,
              '.gladly-unread': {
                '#with-unread-message': {
                  display: 'block',
                },
              },
            },
            EcEnv.NEXT_PUBLIC_COUNTRY !== 'SG' && {
              height: mobile ? 57 : 86,
              paddingTop: mobile ? 0.5 : 1,
            },
          ]}
        >
          <Button
            sx={{
              width: '100%',
              backgroundColor: 'transparent',
              borderRightWidth: 0,
              borderRadius: 0,
              position: 'relative',
              border: 'none',
              color: (theme) => theme.palette.brand.wheat[500],
              padding: mobile ? '6px 10px' : '10px',
              svg: {
                width: mobile ? 30 : 45,
                height: mobile ? 30 : 45,
              },
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            id="custom-gladly-chat-button"
            aria-label="Chat"
            onClick={() => {
              if (EcEnv.NEXT_PUBLIC_COUNTRY === 'SG') {
                window.location.href = 'https://wa.me/6582410030';
              } else {
                window?.Gladly?.show?.();
                if (currentUser) {
                  const name = `${currentUser.firstname || ''} ${currentUser.lastname || ''}`;
                  const email = currentUser.email;
                  const _appData =
                    localStorage.getItem(`castlery.com-${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}`) || '';
                  const appData = JSON.parse(_appData);
                  if (appData?.values?.user?.email === currentUser.email && appData?.values?.user?.name === name) {
                    return;
                  }
                  window.Gladly?.setUser &&
                    window.Gladly?.setUser({
                      name,
                      email,
                    });
                }
              }
            }}
          >
            <Box
              sx={[
                {
                  position: 'absolute',
                  background: (theme) => theme.palette.brand.terracotta[500],
                  border: '2px solid #fff',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, .5)',
                  top: '4px',
                  left: '14px',
                  height: '10px',
                  width: '10px',
                  display: 'none',
                },
                mobile && {
                  top: '1px',
                  left: '5px',
                },
              ]}
              id="with-unread-message"
            />
            {EcEnv.NEXT_PUBLIC_COUNTRY === 'SG' && <Whatsapp />}
            {EcEnv.NEXT_PUBLIC_COUNTRY !== 'SG' && (
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Chat
                  sx={[
                    {
                      width: mobile ? '18px !important' : '28px !important',
                      height: mobile ? '18px !important' : '24px !important',
                    },
                    !mobile && {
                      marginBottom: 0.75,
                    },
                  ]}
                />
                <Typography
                  sx={{
                    color: (theme) => theme.palette.brand.wheat[500],
                    fontSize: mobile ? '12px' : '16px',
                  }}
                >
                  Chat
                </Typography>
              </Stack>
            )}
          </Button>
        </Box>
      )}
    </>
  );
};

export { GladlyBox };
